import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppAdminFormLabel,
  AppBreadcrumb,
  AppButton,
  AppCard,
  AppContainer,
  AppTextField,
} from '@/components/atoms';
import { DeleteFill } from '@/components/atoms/AppIcon';
import { StandardDeleteConfirmContent } from '@/components/molecules';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import useAppToast from '@/hooks/useAppToast';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import {
  getDistanceCache,
  updateDistanceCache,
  deleteDistanceCache,
} from '@/services/admin/distance-caches';
import type { DistanceCacheUpdatePayload } from '@/services/admin/distance-caches';
import { AuditInfo } from '@/components/molecules';
import { LATITUDE_REGEX, LONGITUDE_REGEX } from '@/utils/geoCoordinate';

const SiteAdminDistanceCachesEditPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'super-admin';
  const recordId = Number(id) || 0;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const openStandardDeleteConfirm = useStandardDeleteConfirm();

  const locationState = location.state as { listSearch?: string; fromDetail?: boolean } | null;
  const listSearch = locationState?.listSearch ?? '';
  const fromDetail = locationState?.fromDetail ?? false;
  const listPath = `/${currentSite}/admin/distance_caches${listSearch}`;
  const listPathNoParams = `/${currentSite}/admin/distance_caches`;
  const detailPath = `/${currentSite}/admin/distance_caches/${recordId}`;

  const navigateAfterUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'distance-cache-detail'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'distance-caches'] });
    navigate(listPathNoParams, { replace: true });
  };

  const navigateToList = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'distance-caches'] });
    navigate(listPath, { replace: true });
  };

  const { data, status, isFetching } = useQuery({
    queryKey: ['admin', 'distance-cache-detail', currentSite, recordId],
    queryFn: () => getDistanceCache(currentSite, recordId),
    enabled: !!recordId,
    staleTime: 0,
    gcTime: 0,
  });

  const record = data?.data;
  const isInitialLoading = status === 'pending' || (isFetching && !record);

  const [distance, setDistance] = useState('');
  const [address1, setAddress1] = useState('');
  const [latitude1, setLatitude1] = useState('');
  const [longitude1, setLongitude1] = useState('');
  const [address2, setAddress2] = useState('');
  const [latitude2, setLatitude2] = useState('');
  const [longitude2, setLongitude2] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (record) {
      setDistance(record.distance != null ? String(record.distance) : '');
      setAddress1(record.address1 ?? '');
      setLatitude1(String(record.latitude1 ?? ''));
      setLongitude1(String(record.longitude1 ?? ''));
      setAddress2(record.address2 ?? '');
      setLatitude2(String(record.latitude2 ?? ''));
      setLongitude2(String(record.longitude2 ?? ''));
    }
  }, [record]);

  if (isInitialLoading) return <LoadingTemplate />;
  if (status === 'error' || (!isFetching && !record)) return <SiteAdminNotFoundTemplate />;

  const breadcrumbItems = [
    { title: '距離キャッシュ', href: listPath },
    { title: `編集:${recordId}` },
  ];

  const validateForm = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (!distance.trim()) {
      errors.distance = ['距離は必須です。'];
    } else if (isNaN(Number(distance))) {
      errors.distance = ['距離は数字にしてください。'];
    } else if (Number(distance) < 0) {
      errors.distance = ['距離は0以上の値を入力してください。'];
    } else if (Number(distance) > 1000) {
      errors.distance = ['距離は1000以下にしてください。'];
    }

    if (!address1.trim()) {
      errors.address1 = ['From住所は必須です。'];
    } else if (address1.length > 100) {
      errors.address1 = ['From住所は100文字以下にしてください。'];
    }

    if (!latitude1.trim()) {
      errors.latitude1 = ['From緯度は必須です。'];
    } else if (isNaN(Number(latitude1))) {
      errors.latitude1 = ['From緯度は数字にしてください。'];
    } else if (!LATITUDE_REGEX.test(latitude1.trim())) {
      errors.latitude1 = ['From緯度の書式が正しくありません。'];
    }

    if (!longitude1.trim()) {
      errors.longitude1 = ['From経度は必須です。'];
    } else if (isNaN(Number(longitude1))) {
      errors.longitude1 = ['From経度は数字にしてください。'];
    } else if (!LONGITUDE_REGEX.test(longitude1.trim())) {
      errors.longitude1 = ['From経度の書式が正しくありません。'];
    }

    if (!address2.trim()) {
      errors.address2 = ['To住所は必須です。'];
    } else if (address2.length > 100) {
      errors.address2 = ['To住所は100文字以下にしてください。'];
    }

    if (!latitude2.trim()) {
      errors.latitude2 = ['To緯度は必須です。'];
    } else if (isNaN(Number(latitude2))) {
      errors.latitude2 = ['To緯度は数字にしてください。'];
    } else if (!LATITUDE_REGEX.test(latitude2.trim())) {
      errors.latitude2 = ['To緯度の書式が正しくありません。'];
    }

    if (!longitude2.trim()) {
      errors.longitude2 = ['To経度は必須です。'];
    } else if (isNaN(Number(longitude2))) {
      errors.longitude2 = ['To経度は数字にしてください。'];
    } else if (!LONGITUDE_REGEX.test(longitude2.trim())) {
      errors.longitude2 = ['To経度の書式が正しくありません。'];
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }
    setIsUpdating(true);
    try {
      const payload: DistanceCacheUpdatePayload = {
        distance: Number(distance),
        address1,
        latitude1,
        longitude1,
        address2,
        latitude2,
        longitude2,
      };
      await updateDistanceCache(currentSite, recordId, payload);
      toast.success('更新しました。');
      navigateAfterUpdate();
    } catch (err: unknown) {
      if (isNotFoundError(err)) {
        toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
        navigate(`/${currentSite}/admin/404`, { replace: true });
        return;
      }
      const axiosErr = err as {
        response?: { status?: number; data?: { errors?: Record<string, string[]> } };
      };
      if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors) {
        setFieldErrors(axiosErr.response.data.errors);
        return;
      }
      toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    openStandardDeleteConfirm({
      content: <StandardDeleteConfirmContent />,
      okButtonProps: { loading: isDeleting },
      onOk: async () => {
        setIsDeleting(true);
        try {
          await deleteDistanceCache(currentSite, recordId);
          toast.success('削除しました。');
          navigateToList();
        } catch (err: unknown) {
          if (isNotFoundError(err)) {
            toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
            navigate(`/${currentSite}/admin/404`, { replace: true });
            return;
          }
          toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const clearFieldError = (name: string) => {
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const renderField = (
    label: string,
    name: string,
    value: string,
    onChange: (v: string) => void,
    required = false
  ) => (
    <div className="mb-4">
      <AppAdminFormLabel label={label} required={required} />
      <AppTextField
        value={value}
        onChange={e => {
          onChange(e.target.value);
          clearFieldError(name);
        }}
        status={fieldErrors[name] ? 'error' : undefined}
        className="w-full"
      />
      {fieldErrors[name]?.map((msg, i) => (
        <p key={i} className="mt-1 text-sm text-red-600">
          {msg}
        </p>
      ))}
    </div>
  );

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />

      <AppCard title={`距離キャッシュ編集:${recordId}`}>
        <p className="text-sm">治療院ID：{record?.clinic_number ?? ''}</p>

        <div className="mt-5">
          {renderField('距離', 'distance', distance, setDistance, true)}
          {renderField('From住所', 'address1', address1, setAddress1, true)}
          {renderField('From緯度', 'latitude1', latitude1, setLatitude1, true)}
          {renderField('From経度', 'longitude1', longitude1, setLongitude1, true)}
          {renderField('To住所', 'address2', address2, setAddress2, true)}
          {renderField('To緯度', 'latitude2', latitude2, setLatitude2, true)}
          {renderField('To経度', 'longitude2', longitude2, setLongitude2, true)}

          <div className="flex gap-3 mt-6">
            <AppButton
              icon={<DeleteFill />}
              danger
              loading={isDeleting}
              disabled={isDeleting || isUpdating}
              onClick={handleDelete}
            >
              削除
            </AppButton>
            <AppButton
              type="primary"
              loading={isUpdating}
              disabled={isDeleting}
              onClick={handleUpdate}
            >
              更新
            </AppButton>
            <AppButton
              onClick={() => {
                if (fromDetail) {
                  queryClient.invalidateQueries({ queryKey: ['admin', 'distance-cache-detail'] });
                  navigate(detailPath, { state: { listSearch } });
                } else {
                  queryClient.invalidateQueries({ queryKey: ['admin', 'distance-caches'] });
                  navigate(listPath);
                }
              }}
              disabled={isUpdating || isDeleting}
            >
              前画面に戻る
            </AppButton>
          </div>

          <AuditInfo
            createdBy={record?.created_by}
            updatedBy={record?.updated_by}
            updatedAt={record?.updated_at}
          />
        </div>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminDistanceCachesEditPage;
